/**
 * Samadhan Center — Landing + Buy Flow + Gallery + Certificate + Roadmap
 * ======================================================================
 * The Dream Project · Live 01/01/2031
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Award, Sparkles, MapPin, Phone, IndianRupee, Download, X, TrendingUp, Users, Wallet, ArrowRight } from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';
import ShareButton from '../../components/ShareButton';
import SamadhanLiveTicker from '../../components/SamadhanLiveTicker';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtAT = (n) => `${fmt(n)} AT`;

const useCountdown = (target) => {
  const [t, setT] = useState(() => Math.max(0, new Date(target).getTime() - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setT(Math.max(0, new Date(target).getTime() - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t % 86400000) / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return { d, h, m, s, done: t <= 0 };
};

const Countdown = ({ target, size = 'lg' }) => {
  const { d, h, m, s, done } = useCountdown(target);
  if (done) return <div className="text-emerald-300 font-bold">🎉 LIVE!</div>;
  const cls = size === 'sm' ? 'text-xs' : 'text-2xl md:text-3xl';
  return (
    <div className={`flex gap-2 items-center font-mono font-extrabold ${cls}`} data-testid="samadhan-countdown">
      <Box num={d} label="DAYS" />
      <span className="opacity-60">:</span>
      <Box num={h} label="HRS" />
      <span className="opacity-60">:</span>
      <Box num={m} label="MIN" />
      <span className="opacity-60">:</span>
      <Box num={s} label="SEC" />
    </div>
  );
};
const Box = ({ num, label }) => (
  <div className="text-center px-2">
    <div>{String(num).padStart(2, '0')}</div>
    <div className="text-[9px] opacity-70 font-semibold tracking-wider">{label}</div>
  </div>
);

const StoneStat = ({ label, val }) => (
  <div className="rounded-xl bg-black/40 border border-amber-200/40 p-2">
    <div className="text-[9px] uppercase font-bold text-amber-200/80">{label}</div>
    <div className="text-sm md:text-base font-black text-amber-50 mt-0.5 font-mono">{val}</div>
  </div>
);

const Tickers = () => (
  <>
    <style>{`@keyframes samadhan-blink { 0%,49%{opacity:1}50%,100%{opacity:.35} }`}</style>
    <div className="flex gap-3 justify-center py-2">
      <span className="text-red-400 font-extrabold text-sm tracking-widest" style={{ animation: 'samadhan-blink 1s infinite' }}>🚨 THE DREAM PROJECT 🚨</span>
      <span className="text-amber-400 font-extrabold text-sm tracking-widest" style={{ animation: 'samadhan-blink 1s infinite', animationDelay: '0.5s' }}>⏳ COMING SOON ⏳</span>
    </div>
  </>
);

export default function SamadhanCenter() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [donors, setDonors] = useState([]);
  const [wall, setWall] = useState({ tiers: [], total_members: 0 });
  const [foundation, setFoundation] = useState(null);
  const [founders, setFounders] = useState({ founders: [], total_slots: 10, slots_filled: 0, slots_available: 10 });
  const [myPur, setMyPur] = useState({ purchases: [], total_donated: 0, count: 0 });
  const [busy, setBusy] = useState('');
  const [showCert, setShowCert] = useState(null);
  const load = useCallback(async () => {
    try {
      const [c, s, g, d, mp, nw, fs, fw] = await Promise.all([
        api.get('/samadhan/config'),
        api.get('/samadhan/stats'),
        api.get('/samadhan/gallery'),
        api.get('/samadhan/donors?limit=10'),
        api.get('/samadhan/my-purchases').catch(() => ({ data: { purchases: [], total_donated: 0, count: 0 } })),
        api.get('/samadhan/nameplate-wall').catch(() => ({ data: { tiers: [], total_members: 0 } })),
        api.get('/samadhan/foundation-stone').catch(() => ({ data: null })),
        api.get('/samadhan/founders-wall?limit=10').catch(() => ({ data: { founders: [], total_slots: 10, slots_filled: 0, slots_available: 10 } })),
      ]);
      setCfg(c.data); setStats(s.data); setGallery(g.data.images || []); setDonors(d.data.donors || []); setMyPur(mp.data); setWall(nw.data); setFoundation(fs.data); setFounders(fw.data);
    } catch (e) { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buy = async (pack) => {
    if (!window.confirm(`Donate ${pack.label}?\n\nAap ke AT wallet se ${pack.amount} AT deduct hoga.\n50% Reservoir · 30% Affiliate Network (7-tier) · 20% Admin.`)) return;
    setBusy(pack.id);
    const successStyle = { style: { background: '#065f46', color: '#d1fae5', border: '2px solid #10b981', fontWeight: 700 }, duration: 5000 };
    const errorStyle = { style: { background: '#7f1d1d', color: '#fee2e2', border: '2px solid #ef4444', fontWeight: 700 }, duration: 6000 };
    try {
      const r = await api.post('/samadhan/buy', { pack_id: pack.id });
      toast.success(`🎉 SUCCESS! Certificate ${r.data.cert_id} generated · ${pack.amount} AT donated`, successStyle);
      setShowCert(r.data.purchase);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const status = e.response?.status;
      let msg = detail || 'Purchase failed. Try again.';
      if (status === 402 || /insufficient|balance|kam/i.test(detail)) {
        msg = `❌ INSUFFICIENT FUNDS!\n${detail || `Aapke AT wallet mein ${pack.amount} AT nahi hai.`}\nFund add karein pehle.`;
      } else if (status === 401 || status === 403) {
        msg = `🔒 LOGIN REQUIRED! Pehle login karein donate karne ke liye.`;
      } else if (status === 404) {
        msg = `⚠️ Pack not found: ${pack.label}`;
      }
      toast.error(msg, errorStyle);
    } finally { setBusy(''); }
  };

  if (!cfg) return <div className="min-h-screen pt-24 text-white text-center">Loading Samadhan Center…</div>;
  const { project, packs, roadmap, zones } = cfg;

  const founderNames = (founders.founders || []).map((f) => f.founder_number ? `#${f.founder_number} ${f.name}` : f.name);
  const founderLine = founderNames.length ? `Founded by ${founderNames.join(', ')}` : `${founders.slots_available}/${founders.total_slots} lifetime FOUNDER seats open`;
  const pageTitle = `Samadhan Center · ${founderLine} · The Dream Project`;
  const pageDesc = `SAMADHAN CENTER — a community-funded wellness, entertainment & leisure sanctuary opening 01/01/2031. ${founderLine}. Reservoir: ${fmtAT(stats?.reservoir_balance || 0)} · ${stats?.unique_donors || 0} donors. Claim your lifetime Founder seat — only 10 exist, forever.`;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-950 text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_94259c42-41bd-46e6-b640-f415dc05ba06/artifacts/uxz9qvbs_SAMADHAN_CENTER_peace_mind_celeb%E2%80%A6_202607111933.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_94259c42-41bd-46e6-b640-f415dc05ba06/artifacts/uxz9qvbs_SAMADHAN_CENTER_peace_mind_celeb%E2%80%A6_202607111933.jpeg" />
      </Helmet>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* REFERRAL INVITE BANNER */}
        {(() => {
          const ref = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('ref');
          if (!ref) return null;
          const refFounder = (founders.founders || []).find((f) => (f.username || '').toLowerCase() === ref.toLowerCase());
          const refName = refFounder ? refFounder.name : ref;
          const refBadge = refFounder ? ` · 🏛️ FOUNDER #${refFounder.founder_number}` : '';
          return (
            <div className="rounded-2xl border-2 border-amber-300 relative overflow-hidden animate-pulse-slow"
              style={{ background: 'linear-gradient(135deg, #78350f 0%, #b45309 40%, #fbbf24 100%)', boxShadow: '0 10px 40px rgba(251, 191, 36, 0.35)' }}
              data-testid="samadhan-referral-banner">
              <div className="p-4 md:p-5 flex flex-col md:flex-row items-center justify-between gap-3 relative z-10">
                <div className="text-center md:text-left">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-950 font-black">॥ Personal Invitation ॥</div>
                  <div className="text-lg md:text-2xl font-black text-white mt-1" style={{ textShadow: '1px 2px 4px rgba(0,0,0,0.5)' }}>
                    🙏 <b className="text-yellow-100">{refName}</b>{refBadge} ne aapko invite kiya hai!
                  </div>
                  <div className="text-xs md:text-sm text-amber-950 font-bold mt-1">
                    Sirf <b>{founders.slots_available}/{founders.total_slots}</b> lifetime FOUNDER seats bache hain — hamesha ke liye. Pehle claim karo!
                  </div>
                </div>
                <a href="#samadhan-hero" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="samadhan-hero"]')?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="px-5 py-2.5 rounded-xl bg-slate-950 text-amber-300 font-black text-sm md:text-base whitespace-nowrap hover:bg-black transition-all border-2 border-amber-300"
                  data-testid="samadhan-referral-cta">
                  🕉️ Claim My Seat →
                </a>
              </div>
            </div>
          );
        })()}

        {/* HERO */}
        <SamadhanLiveTicker />
        <div className="rounded-3xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-900/40 via-indigo-900/40 to-emerald-900/30 p-6 md:p-8 relative overflow-hidden" data-testid="samadhan-hero">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-red-300 font-extrabold text-xs tracking-widest">🚨 LIVE FOR PUBLIC FROM {project.public_launch.split('-').reverse().join('/')} 🚨</div>
              <h1 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-amber-300 via-fuchsia-300 to-emerald-300 bg-clip-text text-transparent mt-2">SAMADHAN CENTER</h1>
              <div className="text-lg md:text-xl italic text-slate-200 mt-1">{project.tagline}</div>
              <div className="text-xs text-slate-400 mt-1">Powered by {project.powered_by}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <ATWalletBadge compact />
              <ShareButton type="page" itemName="Samadhan Center" variant="icon" />
            </div>
          </div>
          <Tickers />
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-white/10 bg-black/30 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Total Budget</div>
              <div className="text-2xl font-extrabold text-amber-300">50 CRORE AT</div>
              <div className="text-[10px] text-slate-500">1 AT = 1 INR*</div>
            </div>
            <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300">Reservoir Fund (Live)</div>
              <div className="text-2xl font-extrabold text-emerald-200">{fmtAT(stats?.reservoir_balance)}</div>
              <div className="text-[10px] text-slate-400">{stats?.progress_pct}% of target · {stats?.unique_donors} donors</div>
            </div>
            <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Grand Opening In</div>
              <Countdown target={project.public_launch} />
            </div>
          </div>
        </div>

        {/* DIGITAL FOUNDATION STONE (Shilanyas) */}
        {foundation && (
          <div className="relative flex justify-center py-6" data-testid="samadhan-foundation-stone">
            <style>{`
              @keyframes shilanyas-rotate { 0% { transform: rotateY(-8deg) rotateX(2deg); } 50% { transform: rotateY(8deg) rotateX(-2deg); } 100% { transform: rotateY(-8deg) rotateX(2deg); } }
              @keyframes shilanyas-shimmer { 0% { background-position: 200% 50%; } 100% { background-position: -200% 50%; } }
              .shilanyas-plaque { animation: shilanyas-rotate 8s ease-in-out infinite; transform-style: preserve-3d; }
              .shilanyas-shimmer {
                background: linear-gradient(110deg, transparent 30%, rgba(255, 215, 0, 0.35) 45%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 215, 0, 0.35) 55%, transparent 70%);
                background-size: 200% 100%;
                animation: shilanyas-shimmer 4s linear infinite;
              }
            `}</style>
            <div className="relative w-full max-w-3xl" style={{ perspective: 1200 }}>
              <div className="shilanyas-plaque rounded-3xl relative overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(145deg, #78350f 0%, #b45309 25%, #fbbf24 50%, #b45309 75%, #451a03 100%)', border: '6px double #fde68a', boxShadow: '0 25px 80px rgba(251, 191, 36, 0.35), inset 0 4px 8px rgba(255, 255, 255, 0.25), inset 0 -4px 8px rgba(0, 0, 0, 0.35)' }}>
                <div className="absolute inset-0 shilanyas-shimmer pointer-events-none" />
                <div className="relative p-6 md:p-10 text-center">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-amber-100/90 font-bold">॥ शिलान्यास · Shilanyas ॥</div>
                  <div className="text-2xl md:text-4xl font-black text-amber-50 mt-2 tracking-widest" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.55)' }}>DIGITAL FOUNDATION STONE</div>
                  <div className="text-xs md:text-sm text-amber-100 italic mt-1">Samadhan Center · Paratwada, Maharashtra</div>
                  <div className="mt-6 border-y-2 border-amber-100/40 py-4">
                    <div className="text-[10px] uppercase tracking-widest text-amber-100/80">Laid With The Divine Blessings Of</div>
                    <div className="text-2xl md:text-3xl font-black text-white mt-2" style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.6)' }}>
                      {foundation.shilanyas_by?.name || 'Awaiting the First Divine Base Builder'}
                    </div>
                    {foundation.shilanyas_by?.nameplate && (
                      <div className="text-xs text-amber-200 mt-1">{foundation.shilanyas_by.nameplate} · {foundation.shilanyas_by.pack_label}</div>
                    )}
                    {foundation.foundation_date && (
                      <div className="text-xs text-amber-100/90 mt-1">First Contribution on <b>{foundation.foundation_date}</b></div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-5">
                    <StoneStat label="Total Raised" val={`${Number(foundation.total_raised || 0).toLocaleString('en-IN')} AT`} />
                    <StoneStat label="Reservoir Fund" val={`${Number(foundation.reservoir_balance || 0).toLocaleString('en-IN')} AT`} />
                    <StoneStat label="Donors" val={String(foundation.total_donors || 0)} />
                  </div>
                  <div className="text-[10px] text-amber-100/80 italic mt-4">Grand opening: <b>{foundation.public_launch}</b> · May this foundation bring peace and celebration to all who visit.</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WELCOME + ZONES */}
        <div className="rounded-2xl border border-white/10 bg-black/20 p-6">
          <h2 className="text-2xl font-bold text-white">Welcome to SAMADHAN CENTER</h2>
          <p className="text-slate-300 mt-2 text-sm leading-relaxed">
            Zindagi ki bhaag-daud mein hum do cheezein sabse zyada dhoondhte hain — sukoon (peace) aur khushiyan (celebration).
            Samadhan Center ek aisi akeli destination hai jahan yeh dono ek sath milte hain. Chaahe aapko nature ke beech apne mind aur body ko heal karna ho,
            ya apni family aur doston ke sath ek thrilling weekend enjoy karna ho — Samadhan Center aapke har mood aur har zaroorat ka ultimate solution hai.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
            {zones.map((z) => (
              <div key={z.id} className="rounded-xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-800/40 p-4">
                <div className="text-lg font-bold">{z.title}</div>
                <div className="text-xs text-slate-400 mb-2">{z.subtitle}</div>
                <ul className="space-y-1">{z.services.map((s) => <li key={s} className="text-xs text-slate-200">• {s}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>

        {/* DONATION PACKS */}
        <div className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="text-xl font-bold text-amber-300">💰 Donation Packs</h3>
              <div className="text-[11px] text-slate-400">Each purchase: 50% Reservoir · 30% Affiliate Network (7-tier: 10/7/5/4/3/2/2) · 20% Admin</div>
            </div>
            {myPur.count > 0 && (
              <div className="text-xs text-emerald-300">Aap ne total {fmtAT(myPur.total_donated)} donate kiya · {myPur.count} certificates</div>
            )}
            {/* 🌟 New: The Visionary Landing Page CTA */}
            <Link
              to="/samadhan/vision"
              data-testid="samadhan-vision-cta"
              className="mt-4 mx-auto max-w-xl block rounded-2xl border-2 border-amber-400/60 bg-gradient-to-r from-amber-500/25 via-fuchsia-500/20 to-cyan-500/25 p-4 hover:scale-[1.02] transition group"
            >
              <div className="flex items-center gap-3">
                <div className="text-3xl">🌟</div>
                <div className="flex-1 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-amber-200 font-black">✨ THE VISION · 10X MEGA BONUS</div>
                  <div className="text-sm sm:text-base font-black text-white">Samadhan Center — The Ultimate Celebration of Life</div>
                  <div className="text-[11px] text-cyan-200">10 acre luxury eco-resort · Launch 2031 · VIP Founder Membership</div>
                </div>
                <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition" />
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {packs.map((p) => (
              <div key={p.id} className={`rounded-2xl bg-gradient-to-br ${p.color} p-0.5`} data-testid={`samadhan-pack-${p.id}`}>
                <div className="rounded-2xl bg-slate-950/80 p-4 h-full flex flex-col">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-white/70">{p.tier}{p.nameplate ? ` · ${p.nameplate}` : ''}</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{p.label}</div>
                  <ul className="text-[11px] text-slate-300 space-y-0.5 mt-3 flex-1">
                    {p.benefits.map((b) => <li key={b}>✓ {b}</li>)}
                  </ul>
                  <button
                    onClick={() => buy(p)}
                    disabled={busy === p.id}
                    data-testid={`samadhan-buy-${p.id}`}
                    className="mt-3 w-full py-2 rounded-xl bg-white text-slate-900 font-extrabold text-sm hover:bg-emerald-300 hover:text-emerald-950 focus:bg-emerald-300 focus:outline-none transition disabled:opacity-50"
                  >
                    {busy === p.id ? 'Processing…' : `🙏 Donate ${p.label}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ROADMAP */}
        <div className="rounded-2xl border border-indigo-400/30 bg-indigo-500/5 p-4">
          <h3 className="text-xl font-bold text-indigo-300 mb-3">🗺️ Roadmap · Stages</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {roadmap.map((s) => (
              <div key={s.stage} className="rounded-xl border border-white/10 bg-black/30 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400 font-bold">STAGE {s.stage}</div>
                  <div className="text-[10px] text-slate-500">Target: {s.target_date}</div>
                </div>
                <div className="text-sm font-bold text-white mt-1">{s.title}</div>
                <div className="text-[11px] text-slate-400 mt-1">{s.desc}</div>
                <div className="mt-2"><Countdown target={s.target_date} size="sm" /></div>
              </div>
            ))}
          </div>
        </div>

        {/* GALLERY (compact preview — full page at /samadhan/gallery) */}
        <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-fuchsia-300">📸 Photo Gallery</h3>
            <a href="/samadhan/gallery" data-testid="samadhan-gallery-open" className="text-xs px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white font-bold hover:bg-fuchsia-400">View Full Gallery →</a>
          </div>
          {gallery.length === 0 && <div className="text-xs text-slate-500 text-center py-6">Photos coming soon — admin panel se add kiye ja rahe hain.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {gallery.slice(0, 10).map((im) => (
              <a key={im.id} href="/samadhan/gallery" className="rounded-xl border border-white/10 overflow-hidden group block bg-slate-900/50">
                <img
                  src={im.image_url}
                  alt={im.title || 'Samadhan'}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = 'https://customer-assets.emergentagent.com/job_94259c42-41bd-46e6-b640-f415dc05ba06/artifacts/uxz9qvbs_SAMADHAN_CENTER_peace_mind_celeb%E2%80%A6_202607111933.jpeg'; e.currentTarget.style.opacity = '0.7'; }}
                  className="w-full h-32 object-cover group-hover:scale-105 transition"
                />
                {im.caption && <div className="text-[10px] text-slate-400 p-1 truncate">{im.caption}</div>}
              </a>
            ))}
          </div>
        </div>

        {/* NAMEPLATE WALL — Ceremonial Donor Recognition */}
        {/* FOUNDER'S WALL — pre-launch limited edition (10 lifetime seats) */}
        <div className="rounded-2xl border-2 border-amber-300/50 bg-gradient-to-br from-yellow-950/50 via-amber-950/40 to-yellow-950/50 p-5 relative overflow-hidden" data-testid="samadhan-founders-wall">
          <style>{`
            @keyframes founder-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(253,224,71,0.55); } 50% { box-shadow: 0 0 24px 6px rgba(253,224,71,0.25); } }
            @keyframes founder-flash { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
            .founder-badge { animation: founder-pulse 2.4s ease-in-out infinite; }
            .founder-flash { animation: founder-flash 1.6s ease-in-out infinite; }
          `}</style>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300/90 font-bold">॥ Pre-Launch · Historic ॥</div>
              <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-200 bg-clip-text text-transparent tracking-wider mt-1">
                🏛️ THE FOUNDER'S WALL
              </div>
              <div className="text-xs md:text-sm text-amber-100/80 italic">
                First 10 BASE BUILDERS · Lifetime Golden Badge · Naam sabse upar, hamesha ke liye.
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-amber-200/70 uppercase tracking-widest">Seats Remaining</div>
              <div className="text-3xl md:text-4xl font-black text-amber-100 founder-flash">
                {founders.slots_available} / {founders.total_slots}
              </div>
              <div className="text-[10px] text-red-300 font-bold uppercase mt-1">⚠ Once filled — GONE FOREVER</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {Array.from({ length: founders.total_slots }).map((_, idx) => {
              const f = founders.founders[idx];
              if (f) {
                return (
                  <div key={idx} className="founder-badge rounded-xl border-2 border-amber-300 p-3 relative overflow-hidden text-center"
                    style={{ background: 'linear-gradient(145deg, #78350f 0%, #b45309 40%, #fbbf24 100%)', boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.4)' }}
                    data-testid={`founder-slot-${idx + 1}`}>
                    <div className="absolute top-1 right-1 text-[9px] bg-black/40 text-amber-100 rounded-full px-1.5 py-0.5 font-bold">#{f.founder_number}</div>
                    <div className="text-2xl mb-1">👑</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-200 font-bold">Founder</div>
                    <div className="text-sm md:text-base font-black text-white truncate mt-0.5" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.55)' }}>{f.name}</div>
                    <div className="text-[10px] text-amber-100/85 truncate">{f.pack_label}</div>
                    <div className="text-[9px] text-amber-200/70 mt-0.5">{f.date}</div>
                  </div>
                );
              }
              return (
                <div key={idx} className="rounded-xl border-2 border-dashed border-amber-500/40 bg-slate-900/60 p-3 relative text-center hover:border-amber-300 hover:bg-amber-950/40 transition-all cursor-pointer"
                  onClick={() => document.querySelector('[data-testid="samadhan-hero"]')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid={`founder-slot-empty-${idx + 1}`}>
                  <div className="text-2xl mb-1 opacity-40">🪑</div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400/70 font-bold">Available</div>
                  <div className="text-xs text-amber-100/60 mt-1">Slot #{idx + 1}</div>
                  <div className="text-[10px] text-amber-300 font-bold mt-1 underline">Claim now →</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 text-center text-xs text-amber-100/75">
            Donate <b className="text-amber-200">10 Lakh AT or more</b> to lock a lifetime FOUNDER seat — displayed globally on every Samadhan page, forever. 🕉️
          </div>
        </div>

        <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-950/60 to-amber-950/40 p-4 relative overflow-hidden" data-testid="samadhan-nameplate-wall">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="text-2xl font-black bg-gradient-to-r from-amber-300 to-yellow-200 bg-clip-text text-transparent tracking-wider">🏛️ CEREMONIAL NAMEPLATE WALL</h3>
              <div className="text-[11px] text-amber-200/80 italic">Honoring the Base Builders of Samadhan Center · {wall.total_members} recognized</div>
            </div>
          </div>
          {wall.total_members === 0 && (
            <div className="text-center py-8 text-amber-200/60 text-sm">
              🌟 Bahut jald yahaan pehle &quot;Base Builder&quot; ka naam angkit hoga. Aap bhi is history ka hissa ban sakte ho — upar ke <b>10 Lakh AT</b> ya usse zyada wale packs se apna nameplate reserve karo.
            </div>
          )}
          <div className="space-y-4">
            {wall.tiers.filter((t) => t.count > 0).map((t) => (
              <div key={t.key}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-black tracking-widest px-3 py-1 rounded-full bg-gradient-to-r ${t.gradient} text-white border-2 ${t.border} shadow-lg`}>{t.icon} {t.key} · {t.count}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {t.members.map((m) => (
                    <div key={m.user_id} className={`rounded-xl bg-gradient-to-br ${t.gradient} p-0.5 shadow-xl shadow-amber-500/20`}>
                      <div className="rounded-xl bg-slate-950/90 p-3 h-full relative overflow-hidden">
                        <div className="absolute top-0 right-0 text-4xl opacity-10">{t.icon}</div>
                        <div className={`text-[9px] uppercase font-bold tracking-widest opacity-70`}>Base Builder ·  {m.pack_label}</div>
                        <div className="text-base font-black text-white mt-0.5 truncate">{m.name}</div>
                        {m.username && <div className="text-[10px] text-amber-200/70">@{m.username}</div>}
                        <div className="mt-2 flex items-baseline justify-between gap-2">
                          <span className="text-lg font-black text-amber-300">{Number(m.total_donated).toLocaleString('en-IN')} AT</span>
                          <span className="text-[9px] text-slate-500 font-mono truncate">{m.cert_id}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MY CERTIFICATES */}
        {myPur.purchases.length > 0 && (
          <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-4">
            <h3 className="text-xl font-bold text-emerald-300 mb-3">🎖️ My Certificates ({myPur.count})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {myPur.purchases.map((p) => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-black/30 p-3 flex justify-between items-center">
                  <div>
                    <div className="text-sm font-bold">{p.pack_label} · {fmtAT(p.amount)}</div>
                    <div className="text-[10px] text-slate-400">Cert: {p.cert_id} · {p.created_at?.slice(0, 10)}</div>
                  </div>
                  <button onClick={() => setShowCert(p)} className="px-3 py-1 rounded-lg bg-emerald-500 text-white text-xs font-bold">View</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TOP DONORS */}
        {donors.length > 0 && (
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <h3 className="text-xl font-bold mb-3">🏆 Top Donors</h3>
            <div className="space-y-1">
              {donors.map((d, i) => (
                <div key={d.user_id} className="flex justify-between items-center px-3 py-1.5 rounded bg-white/5">
                  <span className="text-sm">#{i + 1} · <b>{d.full_name || d.username || '—'}</b></span>
                  <span className="text-sm font-mono text-amber-300">{fmtAT(d.total)} · {d.packs} packs</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACT */}
        <div className="rounded-2xl border border-white/10 bg-black/40 p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-sm"><MapPin className="w-4 h-4" /> {project.location}</div>
          <div className="flex items-center justify-center gap-2 text-sm mt-1"><IndianRupee className="w-4 h-4" /> Entry Fee: {project.entry_fee}</div>
          <div className="mt-2">
            <div className="text-xs text-slate-400 font-bold">For Bookings & Inquiries:</div>
            <div className="flex gap-3 justify-center mt-1">
              {project.phones.map((p) => (
                <a key={p} href={`tel:${p.replace(/\s+/g, '')}`} className="text-sm text-fuchsia-300 flex items-center gap-1"><Phone className="w-3 h-3" /> {p}</a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CERTIFICATE MODAL */}
      {showCert && <CertificateModal purchase={showCert} onClose={() => setShowCert(null)} disclaimer={cfg.disclaimer} templateId={cfg.cert_template_id || 'appreciation'} customTemplates={cfg.custom_templates || []} tierTemplates={cfg.tier_templates || {}} onRedeemed={() => load()} />}
    </div>
  );
}


const CERT_TEMPLATES = {
  appreciation: {
    label: '🏆 Certificate of Appreciation (Blue/Teal · Recommended)',
    bg: null,
    accent: '#0e7490',
    accent2: '#0891b2',
    gold: '#d4a017',
    textDark: '#0f172a',
    theme: 'teal',
  },
  gold_divine: {
    label: '🕉️ Divine Gold Ornate',
    bg: null,
    accent: '#b45309',
    accent2: '#f59e0b',
    gold: '#fbbf24',
    textDark: '#78350f',
    theme: 'gold',
  },
  royal_purple: {
    label: '👑 Royal Purple (Classic)',
    bg: null,
    accent: '#6d28d9',
    accent2: '#a855f7',
    gold: '#fbbf24',
    textDark: '#1e1b4b',
    theme: 'purple',
  },
};

const CertificateModal = ({ purchase, onClose, disclaimer, templateId = 'appreciation', customTemplates = [], tierTemplates = {}, onRedeemed }) => {
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemForm, setRedeemForm] = useState({ full_name: purchase.full_name || purchase.username || '', phone: '', address: '', preferred_date: '', notes: '' });
  const [redeeming, setRedeeming] = useState(false);
  // 📱 FIT-TO-VIEWPORT: cert always fits width — NO horizontal scroll on any device.
  // Text scales proportionally with viewport. User sees ENTIRE cert at one glance.
  // For full-resolution readable details, user downloads JPG (renders at native 1200×774 @2x DPI).
  const [certScale, setCertScale] = useState(1);
  useEffect(() => {
    const CERT_W = 1200;
    const update = () => {
      const wrap = document.getElementById('samadhan-cert-scale-wrapper');
      const avail = (wrap ? wrap.getBoundingClientRect().width : window.innerWidth - 32) - 2;
      setCertScale(Math.min(1, Math.max(0.15, avail / CERT_W)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const submitRedeem = async () => {
    if (!redeemForm.full_name.trim() || !redeemForm.phone.trim()) {
      toast.error('❌ Full name aur phone number zaroori hai', { style: { background: '#7f1d1d', color: '#fee2e2', border: '2px solid #ef4444', fontWeight: 700 } });
      return;
    }
    if (!window.confirm('Certificate redeem karne ke baad aapke user side se ye hata diya jayega aur admin team aap se contact karegi. Aage badhein?')) return;
    setRedeeming(true);
    try {
      const r = await api.post(`/samadhan/redeem/${purchase.cert_id}`, redeemForm);
      toast.success(`✅ ${r.data.message || 'Redeem request submitted'}`, { style: { background: '#065f46', color: '#d1fae5', border: '2px solid #10b981', fontWeight: 700 }, duration: 6000 });
      // Auto-notify admin via WhatsApp deep-link
      try {
        const adminPhones = (r.data.admin_phones || []).filter(Boolean);
        const primary = (adminPhones[0] || '9579369830').replace(/\D/g, '');
        const num = primary.length === 10 ? '91' + primary : primary;
        const adminMsg = `🎁 *SAMADHAN CENTER — Redemption Request*\n\n` +
          `🆔 Cert: *${purchase.cert_id}*\n` +
          `📦 Pack: ${purchase.pack_label} · ${fmtAT(purchase.amount)} AT\n` +
          (purchase.nameplate ? `🏛️ Tier: ${purchase.nameplate}\n` : '') +
          `\n👤 Name: *${redeemForm.full_name}*\n` +
          `📞 Phone: *${redeemForm.phone}*\n` +
          (redeemForm.preferred_date ? `📅 Date: ${redeemForm.preferred_date}\n` : '') +
          (redeemForm.address ? `📍 Address: ${redeemForm.address}\n` : '') +
          (redeemForm.notes ? `📝 Notes: ${redeemForm.notes}\n` : '') +
          `\n🔗 Admin Panel: ${window.location.origin}/admin/samadhan\n\n_Auto-sent via Anant Web Portal_ 🙏`;
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(adminMsg)}`, '_blank');
      } catch (_) { /* silent — main redeem already success */ }
      setShowRedeem(false);
      if (onRedeemed) onRedeemed(purchase.cert_id);
      onClose();
    } catch (e) {
      toast.error(`❌ ${e.response?.data?.detail || 'Redeem failed'}`, { style: { background: '#7f1d1d', color: '#fee2e2', border: '2px solid #ef4444', fontWeight: 700 }, duration: 6000 });
    } finally { setRedeeming(false); }
  };
  // Tier-based override: if purchase has a nameplate that admin has mapped to a specific template, use that
  const effectiveTemplateId = (purchase.nameplate && tierTemplates && tierTemplates[purchase.nameplate]) || templateId;
  // Support custom templates from admin (merge with built-ins)
  const customMap = Object.fromEntries((customTemplates || []).map((c) => [c.id, {
    label: c.label,
    bg: c.bg_url,
    accent: c.accent || '#0891b2',
    textDark: c.text_dark || '#0f172a',
    signature_name: c.signature_name || 'Anil Tahalani',
    signature_title: c.signature_title || 'THE LIFE MASTER & FOUNDER',
    signature_url: c.signature_url,
    unique_prefix: c.unique_prefix || 'SAMADHAN',
  }]));
  const tpl = customMap[effectiveTemplateId] || CERT_TEMPLATES[effectiveTemplateId] || CERT_TEMPLATES.appreciation;
  const uniquePrefix = tpl.unique_prefix || 'SAMADHAN';
  const uniqueNum = `${uniquePrefix}-${(purchase.cert_id || '').replace(/[^A-Z0-9]/gi, '').slice(-4).toUpperCase() || '0001'}`;
  const sigName = tpl.signature_name || 'Anil Tahalani';
  const sigTitle = tpl.signature_title || 'THE LIFE MASTER & FOUNDER';
  const dateStr = new Date(purchase.created_at || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  const download = async (fmt) => {
    const el = document.getElementById('samadhan-cert-canvas');
    if (!el) return;
    const html2canvas = (await import('html2canvas')).default;
    // 📱 Cert already renders at fixed 1200×774 internally (mobile just scales it visually).
    // For download: temporarily reset scale to 1 so html2canvas captures full-resolution.
    const prevTransform = el.style.transform;
    el.style.transform = 'scale(1)';
    // Give browser a frame to reflow before capture
    await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 50)));
    let canvas;
    try {
      canvas = await html2canvas(el, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        windowWidth: 1400,   // force desktop viewport measurement
        width: 1200,
        height: Math.round(1200 / 1.55),
      });
    } finally {
      // Always restore — even if capture failed
      el.style.transform = prevTransform;
    }
    if (fmt === 'jpg') {
      const url = canvas.toDataURL('image/jpeg', 0.95);
      const a = document.createElement('a'); a.href = url; a.download = `Samadhan-${uniqueNum}.jpg`; a.click();
    } else {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const img = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(img, 'JPEG', 5, 5, 287, 200);
      pdf.save(`Samadhan-${uniqueNum}.pdf`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="max-w-4xl w-full space-y-3" onClick={(e) => e.stopPropagation()}>
        {/* 📱 Fit-to-viewport wrapper — cert always fully visible, no horizontal scroll */}
        <div className="relative">
        <div
          id="samadhan-cert-scale-wrapper"
          className="w-full overflow-hidden rounded-lg"
          style={{
            height: `${Math.round((1200 / 1.55) * certScale)}px`,
          }}
          data-testid="cert-scale-wrapper"
        >
        {/* Inner presentation div — layout dimensions = visual dimensions after scale */}
        <div style={{ width: `${Math.round(1200 * certScale)}px`, height: `${Math.round((1200 / 1.55) * certScale)}px`, position: 'relative' }}>
        {/* CERTIFICATE CANVAS — Pure HTML/CSS · 100% dynamic per user */}
        <div
          id="samadhan-cert-canvas"
          className="relative shadow-2xl"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1200px',
            height: `${Math.round(1200 / 1.55)}px`,
            transform: `scale(${certScale})`,
            transformOrigin: 'top left',
            fontFamily: 'Georgia, "Times New Roman", serif',
            background: tpl.bg
              ? `url("${tpl.bg}") center/cover no-repeat`
              : tpl.theme === 'gold'
                ? 'radial-gradient(ellipse at top right, #fef3c7 0%, #fde68a 25%, #fbbf24 55%, #d97706 100%)'
                : tpl.theme === 'purple'
                  ? 'radial-gradient(ellipse at top right, #ede9fe 0%, #c4b5fd 30%, #7c3aed 70%, #1e1b4b 100%)'
                  : 'radial-gradient(ellipse at top right, #a7f3d0 0%, #67e8f9 20%, #0891b2 55%, #164e63 90%, #082f49 100%)',
            padding: '0',
            overflow: 'hidden',
          }}
          data-testid="samadhan-cert-canvas"
        >
          {tpl.bg ? (
            /* CUSTOM TEMPLATE with admin-uploaded clean bg image — overlay dynamic text only */
            <div className="absolute inset-0 flex flex-col items-center justify-between p-[7%] text-center">
              <div className="w-full flex justify-between items-start text-[10px] md:text-xs font-bold uppercase tracking-wider" style={{ color: tpl.textDark || '#0c4a6e' }}>
                <span className="opacity-0">.</span>
                <span className="text-right">
                  <div className="opacity-70">UNIQUE NO.</div>
                  <div className="text-sm md:text-lg font-black" style={{ color: tpl.textDark || '#164e63' }}>{uniqueNum}</div>
                </span>
              </div>
              <div className="w-full">
                <div className="text-[10px] md:text-sm font-bold tracking-wider mb-1" style={{ color: tpl.textDark || '#0c4a6e' }}>Presented To:</div>
                <div className="font-black tracking-wide leading-tight" style={{ fontSize: 'clamp(20px, 4.5vw, 44px)', color: '#a16207', fontFamily: 'Georgia, serif' }}>
                  {(purchase.full_name || purchase.username || 'Anonymous Donor').toUpperCase()}
                </div>
                {purchase.username && <div className="text-[10px] md:text-xs mt-1" style={{ color: tpl.textDark || '#164e63' }}>ID: {purchase.username}</div>}
              </div>
              <div className="w-full my-1">
                <div className="text-[10px] md:text-xs mb-1" style={{ color: tpl.textDark || '#0f172a' }}>Contribution of</div>
                <div className="text-xl md:text-4xl font-black" style={{ color: '#065f46' }}>{fmtAT(purchase.amount)}</div>
                <div className="text-[10px] md:text-xs" style={{ color: tpl.textDark || '#0f172a' }}>towards <b>{purchase.pack_label}</b> Pack</div>
                {purchase.nameplate && <div className="mt-1 inline-block px-3 py-0.5 rounded-full text-[10px] md:text-xs font-black border-2" style={{ background: 'rgba(180,83,9,0.15)', borderColor: '#a16207', color: '#78350f' }}>🏛️ {purchase.nameplate}</div>}
              </div>
              <div className="w-full flex justify-between items-end text-[9px] md:text-[11px] font-bold">
                <div className="text-left" style={{ color: tpl.textDark || '#164e63' }}>
                  <div className="opacity-70">Certificate ID</div>
                  <div className="font-mono">{purchase.cert_id}</div>
                  <div className="opacity-70 mt-1">Date: <span className="font-mono">{dateStr}</span></div>
                </div>
                <div className="text-right" style={{ color: tpl.textDark || '#0f172a' }}>
                  <div className="italic" style={{ fontFamily: 'Brush Script MT, cursive', fontSize: 'clamp(18px, 2.5vw, 28px)', color: '#1e40af' }}>{sigName}</div>
                  <div className="uppercase tracking-widest opacity-80" style={{ fontSize: 'clamp(11px, 0.9vw, 13px)' }}>{sigTitle}</div>
                </div>
              </div>
            </div>
          ) : (
            /* BUILT-IN TEMPLATE — pure HTML/CSS ornate design */
            <>
              {/* Ornate outer frame */}
              <div style={{ position: 'absolute', inset: '2%', border: `3px solid ${tpl.gold}`, borderRadius: 4, boxShadow: `inset 0 0 0 6px ${tpl.accent}, inset 0 0 0 8px ${tpl.gold}` }} />
              {/* Golden diagonal corner flourishes */}
              {[['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']].map(([v, h]) => (
                <svg key={`${v}-${h}`} viewBox="0 0 100 100" style={{ position: 'absolute', [v]: '2%', [h]: '2%', width: '14%', height: 'auto', transform: `${h === 'right' ? 'scaleX(-1)' : ''} ${v === 'bottom' ? 'scaleY(-1)' : ''}` }}>
                  <path d="M0,0 L100,0 L100,15 Q50,15 15,50 Q15,80 0,100 Z" fill={tpl.gold} opacity="0.85" />
                  <path d="M0,0 L60,0 L60,8 Q30,8 8,30 Q8,50 0,60 Z" fill={tpl.accent2} opacity="0.6" />
                  <circle cx="8" cy="8" r="2" fill={tpl.gold} />
                </svg>
              ))}

              {/* Content grid */}
              <div className="absolute inset-0 flex flex-col items-center text-center" style={{ padding: 'clamp(24px, 4.5%, 60px) clamp(28px, 6%, 80px)', color: tpl.theme === 'gold' ? '#3f2500' : (tpl.theme === 'purple' ? '#fef3c7' : '#e0f2fe') }}>
                {/* Top row: LOTUS/BRAND left · UNIQUE NO. right */}
                <div className="w-full flex items-start justify-between mb-2">
                  <div style={{ width: '25%' }} />
                  <div className="flex flex-col items-center" style={{ flex: 1 }}>
                    {/* Lotus SVG */}
                    <svg viewBox="0 0 100 60" style={{ height: 'clamp(28px, 4.5vw, 46px)', color: tpl.gold }}>
                      <ellipse cx="50" cy="52" rx="25" ry="4" fill={tpl.gold} opacity="0.4"/>
                      <path d="M50 15 Q45 30 50 48 Q55 30 50 15 Z" fill={tpl.gold}/>
                      <path d="M50 20 Q30 32 34 48 Q46 42 50 32 Z" fill={tpl.gold} opacity="0.85"/>
                      <path d="M50 20 Q70 32 66 48 Q54 42 50 32 Z" fill={tpl.gold} opacity="0.85"/>
                      <path d="M50 22 Q22 40 30 52 Q42 48 50 38 Z" fill={tpl.gold} opacity="0.65"/>
                      <path d="M50 22 Q78 40 70 52 Q58 48 50 38 Z" fill={tpl.gold} opacity="0.65"/>
                    </svg>
                    <div style={{ fontSize: 'clamp(14px, 1.7vw, 20px)', letterSpacing: '0.35em', fontWeight: 900, color: tpl.gold, marginTop: 2 }}>SAMADHAN CENTER</div>
                    <div style={{ fontSize: 'clamp(11px, 0.95vw, 12px)', fontStyle: 'italic', letterSpacing: '0.15em', opacity: 0.85, marginTop: 1 }}>&ldquo;Peace of Mind with the Ultimate Celebration of Life&rdquo;</div>
                  </div>
                  <div style={{ width: '25%', textAlign: 'right' }}>
                    <div style={{ fontSize: 'clamp(11px, 0.9vw, 12px)', letterSpacing: '0.2em', opacity: 0.75 }}>UNIQUE NO.</div>
                    <div style={{ fontSize: 'clamp(15px, 1.4vw, 18px)', fontWeight: 900, color: tpl.gold, letterSpacing: '0.1em', fontFamily: 'monospace' }}>{uniqueNum}</div>
                  </div>
                </div>

                {/* Main title */}
                <div style={{ marginTop: 'clamp(4px, 1vw, 10px)' }}>
                  <div style={{ fontSize: 'clamp(22px, 4.6vw, 48px)', fontWeight: 900, letterSpacing: '0.04em', color: tpl.gold, textShadow: `0 2px 6px rgba(0,0,0,0.35), 0 0 1px ${tpl.gold}`, fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    Certificate <span style={{ fontStyle: 'italic', color: tpl.accent2, fontSize: '0.75em' }}>of</span> Appreciation
                  </div>
                  <div style={{ fontSize: 'clamp(14px, 1.3vw, 16px)', letterSpacing: '0.35em', fontWeight: 700, opacity: 0.9, marginTop: 2 }}>HONORING OUR VALUED DONORS</div>
                </div>

                {/* Presented To */}
                <div style={{ marginTop: 'clamp(6px, 1.5vw, 16px)' }}>
                  <div style={{ fontSize: 'clamp(13px, 1.2vw, 14px)', fontStyle: 'italic', opacity: 0.85 }}>Presented To:</div>
                  <div data-testid="cert-user-name" style={{ fontSize: 'clamp(24px, 3.6vw, 40px)', fontWeight: 900, color: tpl.gold, fontFamily: 'Georgia, serif', letterSpacing: '0.02em', textShadow: '0 1px 3px rgba(0,0,0,0.35)', marginTop: 3, borderBottom: `1px solid ${tpl.gold}88`, paddingBottom: 3, display: 'inline-block' }}>
                    {(purchase.full_name || purchase.username || 'Anonymous Donor').toUpperCase()}
                  </div>
                  {purchase.username && (
                    <div style={{ fontSize: 'clamp(12px, 1vw, 13px)', opacity: 0.75, marginTop: 3 }}>Member ID: <span style={{ fontFamily: 'monospace', color: tpl.gold }}>{purchase.username}</span></div>
                  )}
                </div>

                {/* Body paragraph */}
                <div style={{ fontSize: 'clamp(14px, 1.3vw, 15px)', lineHeight: 1.5, maxWidth: '82%', margin: 'clamp(6px, 1.5vw, 14px) auto 0', opacity: 0.95 }}>
                  With profound gratitude for your visionary generosity, dedicated support, and invaluable contribution of{' '}
                  <b data-testid="cert-amount" style={{ color: tpl.gold, fontSize: '1.35em', letterSpacing: '0.02em', textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>{fmtAT(purchase.amount)}</b>
                  {' '}({purchase.pack_label}) to <b>SAMADHAN CENTER&apos;s</b> mission.
                  {purchase.nameplate && (
                    <> Your commitment elevates you to the sacred rank of{' '}
                      <b style={{ color: tpl.gold, textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>🏛️ {purchase.nameplate}</b>.
                    </>
                  )}
                </div>

                {/* Bottom row */}
                <div className="w-full flex items-end justify-between" style={{ marginTop: 'auto', paddingTop: 'clamp(6px, 1.2vw, 12px)' }}>
                  {/* Left: Redeemable seal */}
                  <div style={{ textAlign: 'center', width: '28%' }}>
                    <div style={{ display: 'inline-block', padding: '4px 8px', border: `2px solid ${tpl.gold}`, borderRadius: 4, fontSize: 'clamp(12px, 0.95vw, 13px)', fontWeight: 900, letterSpacing: '0.12em', lineHeight: 1.3, color: tpl.gold }}>
                      REDEEMABLE IN<br />&ldquo;ANANT WEB PORTAL<br />SERVICES&rdquo; ANYTIME
                    </div>
                  </div>
                  {/* Center: Seal */}
                  <div style={{ textAlign: 'center', width: '28%' }}>
                    <svg viewBox="0 0 100 100" style={{ width: 'clamp(110px, 11vw, 140px)', height: 'auto' }}>
                      <circle cx="50" cy="50" r="47" fill="none" stroke={tpl.gold} strokeWidth="2"/>
                      <circle cx="50" cy="50" r="42" fill="none" stroke={tpl.gold} strokeWidth="0.5" opacity="0.7"/>
                      <circle cx="50" cy="50" r="35" fill={tpl.accent} opacity="0.6"/>
                      {/* Mini lotus */}
                      <path d="M50 30 Q46 42 50 55 Q54 42 50 30" fill={tpl.gold}/>
                      <path d="M50 34 Q38 44 40 55 Q47 50 50 44" fill={tpl.gold} opacity="0.9"/>
                      <path d="M50 34 Q62 44 60 55 Q53 50 50 44" fill={tpl.gold} opacity="0.9"/>
                      <text x="50" y="74" fontSize="15" fontWeight="900" fill={tpl.gold} textAnchor="middle" letterSpacing="0.5">SAMADHAN</text>
                      <text x="50" y="87" fontSize="13" fontWeight="700" fill={tpl.gold} textAnchor="middle" letterSpacing="0.5">CENTER</text>
                    </svg>
                  </div>
                  {/* Right: Signature */}
                  <div style={{ textAlign: 'center', width: '32%' }}>
                    {tpl.signature_url ? (
                      <img src={tpl.signature_url} alt="signature" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ height: 'clamp(24px, 4vw, 40px)', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ fontFamily: 'Brush Script MT, "Lucida Handwriting", cursive', fontSize: 'clamp(18px, 3vw, 32px)', color: tpl.gold, fontStyle: 'italic', textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: 1 }}>{sigName}</div>
                    )}
                    <div style={{ borderTop: `1px solid ${tpl.gold}`, marginTop: 3, paddingTop: 2, fontSize: 'clamp(14px, 1.05vw, 15px)', fontWeight: 900, color: tpl.gold }}>{sigName.toUpperCase()}</div>
                    <div style={{ fontSize: 'clamp(11px, 0.85vw, 12px)', letterSpacing: '0.15em', opacity: 0.85 }}>{sigTitle}</div>
                    <div style={{ marginTop: 2, display: 'inline-block', padding: '1px 5px', borderRadius: 3, border: `1px solid ${tpl.gold}`, fontSize: 'clamp(13px, 0.9vw, 14px)', letterSpacing: '0.18em', color: tpl.gold, fontWeight: 900 }}>SIGNED DIGITALLY</div>
                  </div>
                </div>

                {/* Footer: cert id + date */}
                <div className="w-full flex items-center justify-between" style={{ marginTop: 'clamp(4px, 0.8vw, 8px)', fontSize: 'clamp(11px, 0.9vw, 12px)', opacity: 0.75 }}>
                  <div>Certificate ID: <span style={{ fontFamily: 'monospace', color: tpl.gold, fontWeight: 700 }}>{purchase.cert_id}</span></div>
                  <div style={{ fontWeight: 700, letterSpacing: '0.12em' }}>SAMADHAN CENTER · PARATWADA</div>
                  <div>Date: <span style={{ fontFamily: 'monospace', color: tpl.gold, fontWeight: 700 }}>{dateStr}</span></div>
                </div>
              </div>
            </>
          )}
        </div>
        </div>{/* /inner presentation div */}
        </div>{/* /samadhan-cert-scale-wrapper */}
        </div>{/* /relative wrapper */}
        <div className="text-[9px] text-slate-400 italic text-center max-w-2xl mx-auto">*{disclaimer}</div>

        <div className="flex gap-2 justify-center flex-wrap">
          {(() => {
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const donorName = purchase.full_name || purchase.username || 'A proud donor';
            const isFounder = !!purchase.nameplate;
            const shareUrl = `${origin}/samadhan?ref=${purchase.username || ''}&cert=${purchase.cert_id}`;
            const founderMsg = `🏛️ Aaj main SAMADHAN CENTER ka *${purchase.nameplate}* ban gaya! 🕉️\n\n` +
              `✨ ${donorName}\n` +
              `💎 Contribution: ${fmtAT(purchase.amount)} (${purchase.pack_label})\n` +
              `🆔 Certificate: ${purchase.cert_id}\n\n` +
              `🚨 SAMADHAN CENTER — The Dream Project · Live 01/01/2031\n` +
              `Paratwada · Wellness · Entertainment · Leisure\n\n` +
              `Sirf 10 lifetime FOUNDERS honge — hamesha ke liye. Aap bhi bano:\n${shareUrl}`;
            const donorMsg = `🙏 Aaj maine SAMADHAN CENTER ke liye contribution kiya!\n\n` +
              `✨ ${donorName}\n` +
              `💎 ${fmtAT(purchase.amount)} · ${purchase.pack_label}\n` +
              `🆔 Certificate: ${purchase.cert_id}\n\n` +
              `🌟 The Dream Project · Wellness + Entertainment + Leisure\n` +
              `🎉 Grand Opening: 01/01/2031, Paratwada\n\n` +
              `Aap bhi is divine mission mein judiye:\n${shareUrl}`;
            const msg = isFounder ? founderMsg : donorMsg;
            const twMsg = isFounder
              ? `🏛️ I just became a ${purchase.nameplate} of SAMADHAN CENTER — only 10 lifetime FOUNDER seats exist. The Dream Project opens 01/01/2031. 🕉️`
              : `🙏 Just contributed ${fmtAT(purchase.amount)} to SAMADHAN CENTER — The Dream Project opening 01/01/2031. Cert: ${purchase.cert_id}`;
            const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
            const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twMsg)}&url=${encodeURIComponent(shareUrl)}`;
            const copyAll = async () => {
              try { await navigator.clipboard.writeText(msg); toast.success('Share message copied!'); } catch { toast.error('Copy failed'); }
            };
            return (
              <>
                <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="cert-share-whatsapp"
                   className={`px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-1 ${isFounder ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-black animate-pulse' : 'bg-green-500 text-white'}`}>
                  <Sparkles className="w-4 h-4" />
                  {isFounder ? `Share FOUNDER Badge · WhatsApp` : 'Share on WhatsApp'}
                </a>
                <a href={twUrl} target="_blank" rel="noopener noreferrer" data-testid="cert-share-twitter"
                   className="px-4 py-2 rounded-lg bg-sky-500 text-white font-bold text-sm flex items-center gap-1">
                  <Sparkles className="w-4 h-4" /> Twitter/X
                </a>
                <button onClick={copyAll} data-testid="cert-share-copy"
                   className="px-4 py-2 rounded-lg bg-indigo-500 text-white font-bold text-sm flex items-center gap-1">
                  <Sparkles className="w-4 h-4" /> Copy Message
                </button>
              </>
            );
          })()}
          <button onClick={() => download('jpg')} data-testid="cert-dl-jpg" className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-bold text-sm flex items-center gap-1"><Download className="w-4 h-4" /> JPG</button>
          <button onClick={() => download('pdf')} data-testid="cert-dl-pdf" className="px-4 py-2 rounded-lg bg-red-500 text-white font-bold text-sm flex items-center gap-1"><Download className="w-4 h-4" /> PDF</button>
          <button onClick={() => setShowRedeem(true)} data-testid="cert-redeem-btn" className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white font-black text-sm flex items-center gap-1 animate-pulse">
            🎁 REDEEM
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 text-white font-bold text-sm"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* REDEEM MODAL */}
      {showRedeem && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()} data-testid="samadhan-redeem-modal">
          <div className="max-w-lg w-full rounded-2xl border-2 border-orange-400/60 bg-gradient-to-br from-orange-950/80 via-slate-950 to-red-950/70 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black mb-1">॥ Redeem Your Certificate ॥</div>
            <h3 className="text-xl md:text-2xl font-black text-white mb-1">🎁 Certificate Redemption Request</h3>
            <div className="text-xs text-orange-200/90 mb-3">
              Certificate <span className="font-mono text-amber-300">{purchase.cert_id}</span> · {purchase.pack_label} · {fmtAT(purchase.amount)}
            </div>
            <div className="rounded-lg border border-yellow-400/40 bg-yellow-500/10 p-2 mb-3 text-[11px] text-yellow-100">
              ⚠️ Redeem karne ke baad ye certificate aapke dashboard se hat jayega. Team aap se 24-48 hrs mein contact karegi.
            </div>
            <div className="space-y-2">
              <input placeholder="Full Name *" value={redeemForm.full_name} onChange={(e) => setRedeemForm({ ...redeemForm, full_name: e.target.value })} data-testid="redeem-name" className="w-full bg-slate-900 border border-white/20 rounded px-3 py-2 text-sm text-white" />
              <input placeholder="Phone Number *" value={redeemForm.phone} onChange={(e) => setRedeemForm({ ...redeemForm, phone: e.target.value })} data-testid="redeem-phone" className="w-full bg-slate-900 border border-white/20 rounded px-3 py-2 text-sm text-white" />
              <textarea placeholder="Complete Address" value={redeemForm.address} onChange={(e) => setRedeemForm({ ...redeemForm, address: e.target.value })} data-testid="redeem-address" rows={2} className="w-full bg-slate-900 border border-white/20 rounded px-3 py-2 text-sm text-white" />
              <input type="date" placeholder="Preferred Date" value={redeemForm.preferred_date} onChange={(e) => setRedeemForm({ ...redeemForm, preferred_date: e.target.value })} data-testid="redeem-date" className="w-full bg-slate-900 border border-white/20 rounded px-3 py-2 text-sm text-white" />
              <textarea placeholder="Additional Notes / Requirements" value={redeemForm.notes} onChange={(e) => setRedeemForm({ ...redeemForm, notes: e.target.value })} data-testid="redeem-notes" rows={2} className="w-full bg-slate-900 border border-white/20 rounded px-3 py-2 text-sm text-white" />
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setShowRedeem(false)} disabled={redeeming} className="flex-1 px-4 py-2 rounded-lg bg-slate-700 text-white font-bold text-sm disabled:opacity-50">Cancel</button>
              <button onClick={submitRedeem} disabled={redeeming} data-testid="redeem-confirm" className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white font-black text-sm disabled:opacity-50">
                {redeeming ? '⏳ Submitting...' : '✅ Confirm Redemption'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
